import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { isFileDownloadAllowed } from "@entrilia/shared/utilities/fileHelper";
import Breakpoint from "@entrilia/shared/components/Breakpoint";
import InvestorPortalPreviewFileDialog from "../common/InvestorPortalPreviewFileDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl, downloadFileFromUrlViaAxios } from "@entrilia/shared/services/downloadFile";
import { api } from "../../api/client";
import type { FileInfo } from "../../api/types/commonApiTypes";
import type { FundraisingFile } from "../../api/types/fundraisingTypes";
import { impersonationSelector } from "../../store/state/user/selectors";
import { FundraisingDocumentActionsContextProvider } from "./FundraisingDocumentActionsContext";
import FundraisingDocumentsGrid from "./FundraisingDocumentsGrid";

interface Props {
  files: FundraisingFile[];
  fundraisingId: string;
}

const getDownloadUrlForAllFundraisingDocuments = withErrorHandling(
  api.fundraising.getDownloadUrlForAllFundraisingDocuments
);

interface PreviewFileInfo extends FileInfo {
  preventDownload: boolean;
  refresh: () => Promise<FileInfo | undefined>;
}

const getFundraisingDocumentDownloadInfo = withErrorHandling(api.fundraising.getFundraisingDocumentDownloadInfo);

const FundraisingDocuments = ({ files, fundraisingId }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const isImpersonation = useSelector(impersonationSelector);

  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string>();
  const [previewFileInfo, setPreviewFileInfo] = useState<PreviewFileInfo>();

  if (files.length === 0) {
    return null;
  }

  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    const [url, error] = await getDownloadUrlForAllFundraisingDocuments(fundraisingId);
    setIsDownloadingAll(false);

    if (error) {
      sendNotificationError("The documents could not be downloaded");
      logError(error, "[FundraisingDocuments] getDownloadUrlForAllFundraisingDocuments");
      return;
    }

    if (isImpersonation) {
      downloadFileFromUrlViaAxios(url);
    } else {
      await downloadFileFromUrl(url);
    }
  };

  const handleShowFilePreview = async (fileId: string, preventDownload: boolean) => {
    setDownloadingFileId(fileId);
    const [fileInfo, error] = await getFundraisingDocumentDownloadInfo(fundraisingId, fileId);
    setDownloadingFileId(undefined);

    if (error) {
      sendNotificationError("The document could not be previewed");
      logError(error, "[FundraisingDocuments] getFundraisingDocumentDownloadInfo");
      return;
    }

    const refresh = async (): Promise<FileInfo | undefined> => {
      const [freshInfo, refreshError] = await getFundraisingDocumentDownloadInfo(fundraisingId, fileId);
      if (refreshError) {
        logError(refreshError, "[FundraisingDocuments] refresh document URL");
        return undefined;
      }
      return freshInfo;
    };

    setPreviewFileInfo({ ...fileInfo, preventDownload, refresh });
  };

  const isDownloadAllAvailable = files.some((file) => isFileDownloadAllowed(file.accessOptions));

  return (
    <FundraisingDocumentActionsContextProvider
      showFilePreview={handleShowFilePreview}
      downloadingFileId={downloadingFileId}
    >
      <Stack spacing={0.5} sx={{ flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", py: 2, justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5">Documents</Typography>
          <Breakpoint from="lg">
            {isDownloadAllAvailable && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadAll}
                loading={isDownloadingAll}
              >
                Download all
              </Button>
            )}
          </Breakpoint>
        </Box>

        <FundraisingDocumentsGrid files={files} />
      </Stack>
      {previewFileInfo && (
        <InvestorPortalPreviewFileDialog
          url={previewFileInfo.downloadUrl}
          fileName={previewFileInfo.fileName}
          documentViewSessionId={previewFileInfo.documentViewSessionId}
          refreshUrl={previewFileInfo.refresh}
          onClose={() => setPreviewFileInfo(undefined)}
          preventDownload={previewFileInfo.preventDownload}
        />
      )}
    </FundraisingDocumentActionsContextProvider>
  );
};

export default FundraisingDocuments;
